import type { ReactNode } from 'react';

import { useFeatureFlag } from '@crehana/feature-flags/react';
import { ApplicationRoleEnum, ProductInfoProvider } from '@crehana/layouts';

/**
 * `get_sidebar` query variant (v1 vs v2). Sourced from the `SIDEBAR_V2` flag
 * this app already registers on `FeatureFlagProvider` (see `App.tsx`) — same
 * flag key/version pair used by the sibling `frontend-learning` /
 * `frontend-learning-admin` bridges (CORE-1671).
 */
const FEATURE_FLAG_SIDEBAR_V2: [string, 'V5'] = ['SIDEBAR_V2', 'V5'];

type TProductInfoProviderBridgeProps = {
  centralizedOrganizationId?: number;
  organizationSlug?: string;
  children: ReactNode;
};

/**
 * Mounts `@crehana/layouts`' `ProductInfoProvider` so `useProductInfo()`
 * (white-labeled `sidebarItems`) is available to descendants — in particular
 * `useLearnBreadcrumbTrail`, which reads the top-level "learn" product/module
 * label from it instead of a hardcoded literal. `role` is hardcoded to
 * `RoleUser`: this dashboard is exclusively the employee/student-facing side
 * (contrast with `frontend-learning-admin`'s `RoleAdmin` bridge).
 *
 * Renders children as-is (no provider) when `centralizedOrganizationId` isn't
 * resolved yet — same graceful-degradation shape as the admin-side bridge;
 * `useProductInfo()` also returns a safe empty context on its own when called
 * outside a mounted provider, so this is a belt-and-suspenders guard.
 */
export const ProductInfoProviderBridge = ({
  centralizedOrganizationId,
  organizationSlug,
  children,
}: TProductInfoProviderBridgeProps) => {
  const { flagValue: useV2 } = useFeatureFlag(...FEATURE_FLAG_SIDEBAR_V2);

  if (!centralizedOrganizationId) {
    return <>{children}</>;
  }

  return (
    <ProductInfoProvider
      useV2={Boolean(useV2)}
      centralizedOrganizationId={centralizedOrganizationId}
      role={ApplicationRoleEnum.RoleUser}
      organizationSlug={organizationSlug}
    >
      {children}
    </ProductInfoProvider>
  );
};
